import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import type { SelectionContext } from '@atlaskit/editor-common/types';
import type { DocNode } from '@atlaskit/adf-schema';

import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	RemixContainer,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import type { DynamicUiSource } from './common-types.ts';
import { filterRemixFragmentNodes } from './filterRemixFragmentNodes.ts';
import { RendererRemixHighlightStyles } from './RendererRemixHighlightStyles.tsx';
import { useRemixPreviewFlow } from './useRemixPreviewFlow.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import { useRendererRemixHighlight } from './useRendererRemixHighlight.ts';

const constructDynamicUiSource = (
	adfDocument: DocNode,
	selectionContext: SelectionContext | null,
): DynamicUiSource | undefined => {
	const fragmentAdf = filterRemixFragmentNodes(selectionContext?.selectionFragment ?? null);

	return {
		contentType: 'page' as const,
		startLocalId: selectionContext?.localIds?.[0] ?? '',
		endLocalId: selectionContext?.localIds?.[selectionContext.localIds.length - 1] ?? '',
		startIndex: selectionContext?.startIndex ?? 0,
		endIndex: selectionContext?.endIndex ?? -1,
		// When the entire page is selected (e.g. cmd+a), selectionContext is null and
		// selectionFragment is not populated. Fall back to the full document content
		// so that Remix still receives the source content it needs.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		fragmentAdf: fragmentAdf ?? (adfDocument.content as any),
		fullAdf: adfDocument,
	};
};

type RendererRemixCreatorProps = {
	adfDocument: DocNode;
	selectionContext: SelectionContext | null;
	interactionSource?: InteractionSource;
	onClose: (options?: { preserveHighlight?: boolean }) => void;
	onStartThinking: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
};

type RendererRemixModalProps = RendererRemixCreatorProps & {
	isEphemeralViewVisible: boolean;
};

const RendererRemixModal = ({
	adfDocument,
	selectionContext,
	interactionSource,
	onClose,
	onStartThinking,
	onStopThinking,
	isEphemeralViewVisible,
}: RendererRemixModalProps) => {
	const isRemixCancelled = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const selectedSubTypeRef = useRef<string | undefined>(undefined);
	const conversationIdRef = useRef<string | undefined>(undefined);

	const { dynamicUiType } = useStagingAreaState();
	const { openConversationAssistant } = useToggleConversationAssistant();

	// Collapse the highlight overlay after the preview opens.
	const onAfterOpenPreview = useCallback(() => {
		onClose({ preserveHighlight: true });
	}, [onClose]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		onActionReady,
		resetCompletion,
	} = useRemixPreviewFlow({
		entryPoint: 'renderer',
		experienceTracker,
		dynamicUiType,
		selectedSubTypeRef,
		openAIFCStagingModalRef,
		isRemixCancelledRef: isRemixCancelled,
		conversationIdRef,
		onAfterOpenPreview,
	});

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		remixStoreError,
		openAIFCStagingModal,
		closeAIFCStagingModal,
		resetRemixConversationId,
	} = useRemixToAIFC({ onActionReady });
	openAIFCStagingModalRef.current = openAIFCStagingModal;

	// Abort experience on unmount (overlay closed without selecting an option).
	// Uses a ref so the cleanup always calls the latest tracker reference.
	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				// Clear stale completion state from any previous flow.
				resetCompletion();
				onStartThinking();
			} else {
				isRemixCancelled.current = true;
				// Abort the current experience before stopping
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				stopRemixing();
				// Start a new experience for the reopened overlay
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: {
						entryPoint: 'renderer',
						remixSource: 'cancelledRemixThinking',
						originalRemixSource: 'selectionToolbar',
						product: 'confluence',
					},
				});
				onStopThinking({ reopenModal: true });
			}
		},
		[onStartThinking, stopRemixing, onStopThinking, experienceTracker, resetCompletion],
	);

	// Fail experience and clean up thinking UI when Rovo encounters an error
	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
					errorStatusCode: remixStoreError?.status_code,
				},
			});
			remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			onStopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, remixStoreError, onStopThinking, experienceTracker]);

	// When an error occurs during remix, close the thinking bar and ephemeral
	// preview, then open the Rovo chat sidebar so the user can see the error.
	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			// Close the ephemeral preview (staging modal) if it is open
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			// Close the thinking bar and dismiss the remix UI
			onStopThinking();
			onClose();
			// Open the Rovo chat sidebar to display the error information.
			// `openConversationAssistant` is idempotent (no-op if already
			// open), so it is safe to call on every render where
			// `isRemixError` is true — no infinite toggle loop.
			openConversationAssistant('sidebar');
			resetRemixConversationId();
		}
	}, [
		isRemixError,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		openConversationAssistant,
		resetRemixConversationId,
	]);

	const handleOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			selectedSubTypeRef.current = subType;
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type, remixSubtype: subType },
			});

			remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				entryPoint: 'renderer',
				remixType: type,
				remixSubtype: subType,
			});

			const dynamicUiSource = constructDynamicUiSource(adfDocument, selectionContext);
			const conversationChannelId = await sendAIFCPrompt({
				userPrompt,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource,
			});
			// Stash the conversationId so `useRemixPreviewFlow` can include it as
			// `conversationChannelId` on the REMIX_RENDERS_IN_MODAL start attributes
			// once `onActionReady` fires.
			conversationIdRef.current = conversationChannelId;
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { conversationChannelId },
			});
		},
		[sendAIFCPrompt, adfDocument, selectionContext, interactionSource, experienceTracker],
	);

	return (
		<RemixContainer
			onClose={() => onClose()}
			onOptionSelect={handleOptionSelect}
			onRemixFlowActiveChange={onRemixFlowActiveChange}
			isEphemeralViewVisible={isEphemeralViewVisible}
			showProgressBar={showProgressBar}
			isComplete={isComplete}
			onCompleteAnimationFinished={onCompleteAnimationFinished}
		/>
	);
};

export type RemixDisabledReason = 'archived' | 'version-history';

type RendererRemixContextProps = {
	openRemixModal: (
		adfDocument: DocNode,
		selection: SelectionContext | null,
		interactionSource?: InteractionSource,
	) => void;
	clearRemixHighlight: () => void;
	isRemixDisabled: boolean;
	isRemixing: boolean;
	remixDisabledReason?: RemixDisabledReason;
	registerOnModalClose: (callback: () => void) => () => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: false,
	isRemixing: false,
	registerOnModalClose: () => () => {},
});

export const useRendererRemixContext = () => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({ children }: { children: React.ReactNode }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [adfDocument, setAdfDocument] = useState<DocNode | null>(null);
	const [selectionContext, setSelectionContext] = useState<SelectionContext | null>(null);
	const [isThinking, setIsThinking] = useState(false);
	const interactionSourceRef = useRef<InteractionSource | undefined>(undefined);
	const { startHighlight, clearHighlight } = useRendererRemixHighlight();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const onModalCloseCallbacks = useRef<Set<() => void>>(new Set());

	const registerOnModalClose = useCallback((callback: () => void) => {
		onModalCloseCallbacks.current.add(callback);
		return () => {
			onModalCloseCallbacks.current.delete(callback);
		};
	}, []);
	const prevShowSmartCreationModal = useRef(showSmartCreationModal);

	useEffect(() => {
		if (prevShowSmartCreationModal.current && !showSmartCreationModal) {
			clearHighlight();
		}
		prevShowSmartCreationModal.current = showSmartCreationModal;
	}, [showSmartCreationModal, clearHighlight]);

	const openModal = useCallback(
		(
			newAdfDocument: DocNode,
			selection: SelectionContext | null,
			interactionSource?: InteractionSource,
		) => {
			setAdfDocument(newAdfDocument);
			startHighlight();
			setSelectionContext(selection);
			interactionSourceRef.current = interactionSource;
			setIsModalOpen(true);
		},
		[startHighlight],
	);

	const closeModal = useCallback(
		(options?: { preserveHighlight?: boolean }) => {
			if (!options?.preserveHighlight) {
				clearHighlight();
			}
			onModalCloseCallbacks.current.forEach((cb) => cb());
			setAdfDocument(null);
			setSelectionContext(null);
			interactionSourceRef.current = undefined;
			setIsModalOpen(false);
			setIsThinking(false);
		},
		[clearHighlight],
	);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const context = useMemo(() => {
		const isRemixing = isModalOpen || isThinking || showSmartCreationModal;
		return {
			openRemixModal: openModal,
			clearRemixHighlight: clearHighlight,
			isRemixDisabled: false,
			isRemixing,
			registerOnModalClose,
		};
	}, [
		openModal,
		clearHighlight,
		isModalOpen,
		isThinking,
		showSmartCreationModal,
		registerOnModalClose,
	]);

	return (
		<RendererRemixContext.Provider value={context}>
			<RendererRemixHighlightStyles />
			{children}
			{(isModalOpen || isThinking) && adfDocument && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal
						adfDocument={adfDocument}
						selectionContext={selectionContext}
						interactionSource={interactionSourceRef.current}
						onClose={closeModal}
						onStartThinking={onStartThinking}
						onStopThinking={onStopThinking}
						isEphemeralViewVisible={showSmartCreationModal}
					/>
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

const makeDisabledRemixContextValue = (
	reason?: RemixDisabledReason,
): RendererRemixContextProps => ({
	openRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: true,
	isRemixing: false,
	remixDisabledReason: reason,
	registerOnModalClose: () => () => {},
});

const disabledRemixContextValue = makeDisabledRemixContextValue();

export const RendererRemixContainer = ({
	children,
	isDisabled,
	remixDisabledReason,
}: {
	children: React.ReactNode;
	isDisabled?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}) => {
	if (isDisabled) {
		const contextValue = remixDisabledReason
			? makeDisabledRemixContextValue(remixDisabledReason)
			: disabledRemixContextValue;
		return (
			<RendererRemixContext.Provider value={contextValue}>{children}</RendererRemixContext.Provider>
		);
	}

	return <RendererRemixContainerImpl>{children}</RendererRemixContainerImpl>;
};
